import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { OfflineDbService } from './offline-db.service';
import { OfflineCryptoService, MEDIA_CHUNK_SIZE_BYTES } from './offline-crypto.service';
import { MediaRecord } from './offline-db.types';
import { hasRoomFor } from './offline-quota';

export interface RenditionManifestEntry {
  width: number;
  height: number;
  bitrate: number;
  bytes: number;
  label: string;
  url: string;
  // Always store a rendition under THIS, never a version resolved separately — see the note
  // on the server-side type. Every writer takes it from here.
  version: string;
}

export type RenditionStatusName = 'notrequested' | 'building' | 'ready' | 'noteligible' | 'failed';

export interface StaleVideo {
  moduleid: number;
  uploadid: string;
  resourceId: string;
  savedVersion: string;
  liveVersion: string;
}

export interface RenditionsResponse {
  renditions: RenditionManifestEntry[];
  status: RenditionStatusName;
  // Changes whenever the video is reprocessed or its renditions rebuilt, so a saved copy can
  // be recognised as superseded. 'unversioned' only for media that has never been processed.
  version: string;
}

// Shared by P4 (documents/images) and P5 (video) — gives OfflineCryptoService's
// encryptChunked/decryptChunked (built in P1, uncalled until now) their first callers.
// Attachment/video bytes come from media.impata.com directly, not environment.ImpataAppAPIRoot
// (docs/offline-mode-plan.md §7 P4), via a raw fetch() rather than HttpClient, so
// JwtInterceptorService never sees these requests — this service downloads explicitly.
@Injectable({
  providedIn: 'root',
})
export class OfflineMediaService {
  constructor(private offlineDb: OfflineDbService, private offlineCrypto: OfflineCryptoService, private http: HttpClient) {}

  // GET /offline/renditions — read only, never schedules work. An empty renditions array plus
  // status 'notrequested' means the offline copies have not been built yet and can be asked for;
  // 'noteligible' means this media can never have them (audio) and must not be asked again.
  async getRenditions(moduleid: number, uploadid: string): Promise<RenditionsResponse> {
    const resp: any = await this.http
      .get(environment.ImpataAppAPIRoot + `offline/renditions?moduleid=${moduleid}&uploadid=${uploadid}`)
      .toPromise();
    const version: string = resp?.version ?? 'unversioned';
    // Fall back to the envelope version per entry. A client deployed ahead of the server would
    // otherwise get undefined here and write it straight into the storage key, producing a
    // record nothing could ever find again.
    const renditions: RenditionManifestEntry[] = (resp?.renditions ?? []).map((r: RenditionManifestEntry) => ({ ...r, version: r.version ?? version }));
    return { renditions, status: resp?.status ?? 'notrequested', version };
  }

  // POST /offline/requestrenditions — queues the build. The worker remuxes the MP4s out of the
  // HLS that already exists, so this is cheap; it is still a POST because it schedules work and
  // must not be triggered by merely opening a page.
  async requestRenditions(moduleid: number, uploadid: string): Promise<RenditionStatusName> {
    const body = new HttpParams().set('moduleid', String(moduleid)).set('uploadid', uploadid);
    const resp: any = await this.http
      .post(environment.ImpataAppAPIRoot + `offline/requestrenditions?${body.toString()}`, null)
      .toPromise();
    return resp?.status ?? 'failed';
  }

  private async readBodyWithProgress(resp: Response, totalBytes: number, onProgress?: (bytesReceived: number, totalBytes: number) => void): Promise<ArrayBuffer> {
    if (!resp.body || !onProgress) return resp.arrayBuffer();

    const reader = resp.body.getReader();
    const parts: Uint8Array[] = [];
    let received = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      parts.push(value);
      received += value.byteLength;
      onProgress(received, totalBytes);
    }
    const combined = new Uint8Array(received);
    let offset = 0;
    for (const part of parts) {
      combined.set(part, offset);
      offset += part.byteLength;
    }
    return combined.buffer;
  }

  private manifestKey(school: string, user: number, resourceId: string, rendition: string, version: string): string {
    return `${school}:${user}:media:${resourceId}:${rendition}:${version}:manifest`;
  }

  private chunkKey(school: string, user: number, resourceId: string, rendition: string, version: string, index: number): string {
    return `${school}:${user}:media:${resourceId}:${rendition}:${version}:chunk:${index}`;
  }

  // Which saved videos the server has since reprocessed. Needs a network - callers run it on
  // reconnect and when the downloads hub opens. Never throws: a video we cannot reach is
  // simply not reported as stale.
  async findStaleSavedVideos(school: string, user: number): Promise<StaleVideo[]> {
    const manifests = await this.offlineDb.listMediaManifests(school, user).catch(() => []);
    const stale: StaleVideo[] = [];

    for (const manifest of manifests) {
      // resourceId is `${moduleid}:${uploadid}` - see media.component.ts.
      const split = manifest.resourceId.indexOf(':');
      if (split < 0) continue;
      const moduleid = Number(manifest.resourceId.slice(0, split));
      const uploadid = manifest.resourceId.slice(split + 1);
      if (!Number.isFinite(moduleid) || !uploadid) continue;

      try {
        const live = await this.getRenditions(moduleid, uploadid);
        // 'unversioned' means the server has never processed this video, or the copy predates
        // versioning. Either way we cannot actually tell whether it is out of date, and
        // claiming it is produces a notification the student can never clear.
        if (manifest.version === 'unversioned' || live.version === 'unversioned') continue;
        if (live.version !== manifest.version) {
          stale.push({ moduleid, uploadid, resourceId: manifest.resourceId, savedVersion: manifest.version, liveVersion: live.version });
        }
      } catch {
        continue; // unreachable right now; not evidence of staleness
      }
    }

    return stale;
  }

  // Removes a stored rendition and every one of its chunks. Called before re-downloading a
  // superseded copy — without it each update would leave the previous version's chunks behind
  // forever, since they live under a different key.
  async deleteStoredRendition(school: string, user: number, resourceId: string, rendition: string, version: string): Promise<void> {
    const manifestKey = this.manifestKey(school, user, resourceId, rendition, version);
    const manifest = await this.offlineDb.getMedia(manifestKey);
    const chunkCount = manifest?.chunkCount ?? 0;
    for (let i = 0; i < chunkCount; i++) {
      await this.offlineDb.deleteMedia(this.chunkKey(school, user, resourceId, rendition, version, i));
    }
    await this.offlineDb.deleteMedia(manifestKey);
  }

  async isDownloaded(school: string, user: number, resourceId: string, rendition: string, version: string): Promise<boolean> {
    const manifest = await this.offlineDb.getMedia(this.manifestKey(school, user, resourceId, rendition, version));
    return !!manifest;
  }

  // Downloads a whole file in one shot — a plain fetch(), deliberately NOT a ranged request:
  // media.impata.com's CORS policy was tested directly during planning and does not appear
  // to allow the Range request header cross-origin (see the P5 plan section on this), so a
  // ranged download would fail preflight in a real browser. This trades resumability for
  // something that actually works today; swapping in Range support is a follow-up once that
  // CORS policy is fixed, not a code change here. Progress is still reported (onProgress)
  // via the response body's own streaming reader, which needs no extra request headers.
  //
  // Acceptable to buffer the whole plaintext here for documents/images and — since this is a
  // whole-file, non-ranged download — also for the one video rendition file itself: P0's
  // memory-ceiling finding is about DECRYPTING and holding a large file for BLOB PLAYBACK,
  // not about a one-time download-then-chunk-to-storage pass, which is what this is.
  async downloadAndStore(
    url: string,
    school: string,
    user: number,
    resourceId: string,
    rendition: string,
    version: string,
    mimeType: string,
    onProgress?: (bytesReceived: number, totalBytes: number) => void,
  ): Promise<void> {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`download failed: ${url} -> HTTP ${resp.status}`);

    const totalBytes = Number(resp.headers.get('content-length')) || 0;
    if (totalBytes && !(await hasRoomFor(totalBytes))) {
      throw new Error(`not enough storage: need ${totalBytes} bytes`);
    }

    const buf = await this.readBodyWithProgress(resp, totalBytes, onProgress);

    if (!totalBytes && !(await hasRoomFor(buf.byteLength))) {
      throw new Error(`not enough storage: need ${buf.byteLength} bytes`);
    }

    const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
    const chunkCount = Math.max(1, Math.ceil(buf.byteLength / MEDIA_CHUNK_SIZE_BYTES));
    let index = 0;
    for await (const encrypted of this.offlineCrypto.encryptChunked(deviceKey, buf)) {
      const record: MediaRecord = {
        key: this.chunkKey(school, user, resourceId, rendition, version, index),
        recordType: 'chunk',
        school,
        user,
        resourceId,
        rendition,
        version,
        index,
        iv: encrypted.iv,
        ciphertext: encrypted.ciphertext,
        savedAt: Date.now(),
      };
      await this.offlineDb.putMedia(record);
      index++;
    }

    const manifest: MediaRecord = {
      key: this.manifestKey(school, user, resourceId, rendition, version),
      recordType: 'manifest',
      school,
      user,
      resourceId,
      rendition,
      version,
      mimeType,
      totalBytes: buf.byteLength,
      chunkSizeBytes: MEDIA_CHUNK_SIZE_BYTES,
      chunkCount,
      savedAt: Date.now(),
    };
    await this.offlineDb.putMedia(manifest);
  }

  // Decrypt-all-then-Blob — acceptable for documents/images, not for lesson-length video
  // (P0's finding: ~1.08x plaintext-to-heap, ruled out for anything large).
  async readAsBlob(school: string, user: number, resourceId: string, rendition: string, version: string): Promise<Blob | null> {
    const manifest = await this.offlineDb.getMedia(this.manifestKey(school, user, resourceId, rendition, version));
    if (!manifest || manifest.chunkCount == null) return null;

    const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
    const parts: ArrayBuffer[] = [];
    for (let i = 0; i < manifest.chunkCount; i++) {
      const chunk = await this.offlineDb.getMedia(this.chunkKey(school, user, resourceId, rendition, version, i));
      if (!chunk || !chunk.iv || !chunk.ciphertext) return null;
      parts.push(await this.offlineCrypto.decryptBlob(deviceKey, chunk.iv, chunk.ciphertext));
    }
    return new Blob(parts, { type: manifest.mimeType || 'application/octet-stream' });
  }

  // For readAsBlob() callers that want a revocable object URL (e.g. an <a href> rewrite) —
  // caller is responsible for URL.revokeObjectURL when the element/page goes away.
  async objectUrlFor(school: string, user: number, resourceId: string, rendition: string, version: string): Promise<string | null> {
    const blob = await this.readAsBlob(school, user, resourceId, rendition, version);
    return blob ? URL.createObjectURL(blob) : null;
  }

  async deleteDownload(school: string, user: number, resourceId: string, rendition: string, version: string): Promise<void> {
    const manifest = await this.offlineDb.getMedia(this.manifestKey(school, user, resourceId, rendition, version));
    if (manifest?.chunkCount != null) {
      for (let i = 0; i < manifest.chunkCount; i++) {
        await this.offlineDb.deleteMedia(this.chunkKey(school, user, resourceId, rendition, version, i));
      }
    }
    await this.offlineDb.deleteMedia(this.manifestKey(school, user, resourceId, rendition, version));
  }
}
